import { Model } from '@app/shared/models/model.interface';
import { SessionModel } from './session.model';
import { SessionAddressModel } from './session-address.model';
import {
  DiscountInfo,
  parseDiscountInfo,
} from '@app/akita/router/models/discount-info.model';
import { PriceModel } from '@app/shared/models/api/price.model';
import { Product } from '../../products/models/product.model';
import { ContactDetails } from './payment-request-info.model';
import { Insurance, parseInsuranceList } from '../../products/models/insurance.model';
import { ApiError, parseApiError } from '@app/shared/models/api/api-error.model';

export interface OrderStates {
  logistics: string;
  order: string;
  payment: string;
}

export interface Package {
  order: Array<Product>;
  orderState: string;
  trackingUrl: string;
  trackingAWB?: string | null;
  returningUrl?: string | null;
  returningAWB?: string | null;
}

export class PaymentOrderModel implements Model {
  public id: string;
  public order: Array<Product>;
  public insurances: Array<Insurance> | null;
  public email: string | null;
  public packages: Array<Package> | null;
  public shippingAddress: SessionAddressModel | null;
  public billingAddress: SessionAddressModel | null;
  public customerDetails: { name: string; contact: ContactDetails } | null;
  public customerId: string | null;
  public paymentId: string | null;
  public totalPrice: PriceModel | null;
  public storeCountry: string;
  public storeLanguage: string;
  public storeCurrency: string;
  public discount: DiscountInfo | null;
  public states: OrderStates | null;
  public modifiedAt: Date | null;
  public createdAt: Date | null;
  public isProtected: boolean;
  public protectionPrice: PriceModel | null;
  public paymentError?: ApiError | null;

  /**
   * Constructor to initialize the properties
   * - Make sure the properties are initialized (to avoid unexpected undefined values)
   * @constructor
   */
  constructor() {
    this.id = '';
    this.order = [];
    this.insurances = null;
    this.email = '';
    this.billingAddress = null;
    this.shippingAddress = null;
    this.customerDetails = null;
    this.customerId = null;
    this.paymentId = null;
    this.modifiedAt = null;
    this.totalPrice = new PriceModel();
    this.storeCountry = '';
    this.storeLanguage = '';
    this.storeCurrency = '';
    this.discount = null;
    this.states = null;
    this.createdAt = null;
    this.packages = null;
    this.isProtected = false;
    this.protectionPrice = null;
  }

  /**
   * Helper function to sanitize the data that comes from the Backend.
   * @param data The Raw JSON Object
   * @returns {PaymentConfigurationModel} An instance of the payment configuration class.
   */
  public static fromJson(data?: Record<string, any> | null): PaymentOrderModel | null {
    let order: PaymentOrderModel | null = null;
    if (data) {
      order = new PaymentOrderModel();
      order.id = data.id || '';
      if (data.order.length) {
        order.order = SessionModel.parseShortProductList(data.order || []);
      }

      if (data.insurance && data.insurance.length) {
        order.insurances = parseInsuranceList(data.insurance) || null;
      }

      order.email = data.email || '';
      if (data.customerDetails || data.customer_details) {
        order.customerDetails = data.customerDetails || data.customer_details || null;
        // order.customerDetails.contact = parsePhoneInfo(data.customer_details.contact);
      }
      if (data.paymentId || data.payment_id) {
        order.paymentId = data.paymentId || data.payment_id || null;
      }
      order.billingAddress = SessionAddressModel.fromJson(
        data.billingAddress || data.billing_address
      );
      order.shippingAddress = SessionAddressModel.fromJson(
        data.shippingAddress || data.shipping_address
      );
      // order.phone = parsePhoneInfo(data.phone);
      order.totalPrice = PriceModel.fromJson(data.totalPrice || data.total_price);
      order.storeCountry = data.storeCountry || data.store_country || '';
      order.storeLanguage = data.storeLanguage || data.store_language || '';
      order.storeCurrency = data.storeCurrency || data.store_currency || '';
      order.discount = parseDiscountInfo(data.discount);
      order.states = data.states || null;
      order.createdAt = data.createdAt || null;
      order.modifiedAt = data.modifiedAt || null;

      order.packages = PaymentOrderModel.parsePackages(data.packages);

      if (data.protection_price || data.protectionPrice) {
        order.protectionPrice = PriceModel.fromJson({
          amount: data.protectionPrice || data.protection_price || 0,
          currency: order.storeCurrency,
        });
        order.isProtected = true;
      }

      // error response from STC PAY
      if (
        (data.paymentError || data.payment_error) &&
        (data.paymentError?.code || data.payment_error?.code)
      ) {
        order.paymentError = parseApiError(data.paymentError || data.payment_error);
      }
    }
    return order;
  }

  /**
   * Helper function to sanitize the data that comes from the Backend.
   * @param data The Raw JSON Object
   * @returns {PaymentConfigurationModel} An instance of the payment configuration class.
   */
  public static fromJsonList(
    data?: Array<Record<string, any>> | null
  ): Array<PaymentOrderModel> {
    const orders: Array<PaymentOrderModel> = [];
    if (data) {
      data.forEach((item: Record<string, any>) => {
        if (item) {
          orders.push(PaymentOrderModel.fromJson(item) || new PaymentOrderModel());
        }
      });
    }
    return orders;
  }

  public static parsePackages(data: any): Array<Package> | null {
    if (!data) {
      return null;
    }

    const packages: Array<Package> = [];

    if (data.length) {
      data.forEach((item: any) => {
        if (item) {
          packages.push({
            order: SessionModel.parseShortProductList(item.order || []),
            orderState: item.orderState || item.order_state || '',
            trackingUrl: item.trackingUrl || '',
            trackingAWB: item.trackingAWB || null,
          });
        }
      });
    }

    return packages;
  }
}
